import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth, apiErr } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try { await login(email, password); nav("/"); }
    catch (err) { setError(apiErr(err)); }
    finally { setLoading(false); }
  };

  const demo = [
    { r: "Amministratore", e: "riccardo.mascheroni@ludovicamascheroni.it", p: "Mascheroni2026!" },
    { r: "Responsabile Logistica", e: "logistica@ludovicamascheroni.it", p: "Demo2026!" },
    { r: "Project Manager", e: "pm@ludovicamascheroni.it", p: "Demo2026!" },
    { r: "Autista", e: "marco.rossi@ludovicamascheroni.it", p: "Autista2026!" },
  ];

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-lm-cream">
      <div className="hidden lg:block relative overflow-hidden bg-lm-brown">
        <img src="https://images.unsplash.com/photo-1716755917576-7ab0b4913929?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Cuoio" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <img src="/logo-mascheroni.webp" alt="Ludovica Mascheroni" className="h-10 w-auto brightness-0 invert" />
          <div>
            <h1 className="font-display text-5xl text-lm-cream leading-tight">Pianificazione<br />giri sartoriale</h1>
            <p className="text-lm-sand mt-4 max-w-md">Ritiri, consegne e trasferimenti tra fornitori, boutique e sedi — organizzati con cura artigianale.</p>
          </div>
          <p className="text-xs text-lm-sand/70 tracking-widest uppercase">Rimalcuoio · Ludovica Mascheroni</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm lm-anim">
          <img src="/logo-mascheroni.webp" alt="LM" className="h-9 mb-8 lg:hidden" />
          <h2 className="font-display text-3xl text-lm-text mb-1">Accedi</h2>
          <p className="text-sm text-lm-muted mb-8">Inserisci le tue credenziali per continuare.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-lm-text2">Email</Label>
              <Input id="email" type="email" data-testid="login-email" value={email} required
                onChange={(e) => setEmail(e.target.value)} className="mt-1.5 bg-white border-lm-sandsoft" placeholder="nome@ludovicamascheroni.it" />
            </div>
            <div>
              <Label htmlFor="password" className="text-lm-text2">Password</Label>
              <Input id="password" type="password" data-testid="login-password" value={password} required
                onChange={(e) => setPassword(e.target.value)} className="mt-1.5 bg-white border-lm-sandsoft" placeholder="••••••••" />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-[#991B1B] bg-[#FDF2F2] border border-[#F8B4B4] rounded-lg px-3 py-2">{error}</p>}
            <Button type="submit" data-testid="login-submit" disabled={loading}
              className="w-full bg-lm-brown hover:bg-lm-brownh text-lm-cream h-11">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Entra
            </Button>
            <div className="text-center">
              <Link to="/forgot-password" className="text-sm text-lm-terra hover:underline" data-testid="forgot-link">Password dimenticata?</Link>
            </div>
          </form>

          <div className="mt-8 pt-6 border-t border-lm-sandsoft">
            <p className="text-xs text-lm-muted mb-2 font-semibold uppercase tracking-wide">Accessi dimostrativi</p>
            <div className="grid gap-1.5">
              {demo.map((d) => (
                <button key={d.e} data-testid={`demo-${d.r.toLowerCase().replace(/[^a-z]/g, "")}`}
                  onClick={() => { setEmail(d.e); setPassword(d.p); }}
                  className="text-left text-xs px-3 py-2 rounded-lg bg-white border border-lm-sandsoft hover:border-lm-sand transition-colors">
                  <span className="font-semibold text-lm-brown">{d.r}</span>
                  <span className="text-lm-muted block">{d.e}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
